import { ApplicationState } from '../../store';
import { IExtensibleEntity } from '../../entities/common';
import { bindActionCreators } from 'redux';
import * as PortfoliosListStore from '../../store/PortfoliosListStore';
import * as ProgramsListStore from '../../store/ProgramsListStore';
import * as ProjectsListStore from '../../store/ProjectsListStore';
import * as ArchivedProjectsListStore from '../../store/ArchivedProjectsListStore';
import * as ResourcesListStore from '../../store/ResourcesListStore';
import * as ChallengesListStore from '../../store/ChallengesListStore';
import * as IdeasListStore from '../../store/IdeasListStore';
import * as ObjectivesListStore from '../../store/ObjectivesListStore';
import * as RoadmapsListStore from '../../store/RoadmapsListStore';
/**
 * Utility for resolving entities from Redux store by type and ID
 */
export class EntityResolver {
    /**
     * Gets an entity from the appropriate store based on entity type and ID
     */
    static getEntityFromStore(state: ApplicationState, entityType: string, entityId: string): IExtensibleEntity | null {
        try {
            switch (entityType.toLowerCase()) {
                case 'portfolio':
                    return state.portfolios?.byId?.[entityId] || null;
                case 'program':
                    return state.programs?.byId?.[entityId] || null;
                case 'project':
                    return state.projectsList?.byId?.[entityId] || null;
                case 'archivedproject':
                    return state.archivedProjectsList?.byId?.[entityId] || null;
                case 'resource':
                    return state.resources?.byId?.[entityId] || null;
                case 'challenge':
                    return state.challenges?.byId?.[entityId] || null;
                case 'idea':
                    return state.ideas?.byId?.[entityId] || null;
                case 'objective':
                    return state.objectives?.byId?.[entityId] || null;
                case 'roadmap':
                    return state.roadmaps?.byId?.[entityId] || null;
                default:
                    return null;
            }
        } catch (error) {
            // Swallow and treat as not found; logging is omitted to avoid noisy console output
            return null;
        }
    }

    /**
     * Gets an entity from store with fallback to a minimal entity object if not found
     */
    static getEntityWithFallback(state: ApplicationState, entityType: string, entityId: string): IExtensibleEntity | null {
        if (!entityType || !entityId) {
            return null;
        }

        // Try to get entity from store
        const entity = this.getEntityFromStore(state, entityType, entityId);
        return entity;
    }
    
    static loadEntity(entityType: string, entityId: string) {
        switch (entityType.toLowerCase()) {
            case 'portfolio':
                return PortfoliosListStore.actionCreators.loadPortfolio(entityId);
            case 'program':
                return ProgramsListStore.actionCreators.loadProgram(entityId);
            case 'project':
                return ProjectsListStore.actionCreators.loadProject(entityId);
            case 'archivedproject':
                return ArchivedProjectsListStore.actionCreators.loadProject(entityId);
            case 'resource':
                return ResourcesListStore.actionCreators.loadResource(entityId);
            case 'challenge':
                return ChallengesListStore.actionCreators.loadChallenge(entityId);
            case 'idea':    
                return IdeasListStore.actionCreators.loadIdea(entityId);
            case 'objective':
                return ObjectivesListStore.actionCreators.loadObjective(entityId);
            case 'roadmap':
                return RoadmapsListStore.actionCreators.loadRoadmap(entityId);
            default:
                return null;
        }
    }

}

/**
 * Type for components that need entity resolution props
 */
export interface WithEntityResolution {
    entity: IExtensibleEntity | null;
    entityType?: string;
    loadEntity: () => void;
}

/**
 * Enhanced HOC that provides both state and dispatch props for entity resolution
 */
export const withEntityResolution = <T extends WithEntityResolution>(
    mapAdditionalStateProps: (state: ApplicationState) => Omit<T, keyof WithEntityResolution>,
    additionalActionCreators?: any
) => {
    const mapStateToProps = (state: ApplicationState, ownProps: { match: { params: { entityType?: string; entityId?: string } } }) => {
        const { entityType, entityId } = ownProps.match.params;
        const entity = EntityResolver.getEntityWithFallback(state, entityType ?? '', entityId ?? '');
        
        const additionalProps = mapAdditionalStateProps(state);
        
        return {
            ...additionalProps,
            entity,
            entityType,
        };
    };

    const mapDispatchToProps = (dispatch: any, ownProps: { match: { params: { entityType?: string; entityId?: string } } }) => {
        const { entityType, entityId } = ownProps.match.params;
        
        const entityProps = {
            loadEntity: () => {
                const action = EntityResolver.loadEntity(entityType ?? '', entityId ?? '');
                if (action) {
                    dispatch(action);
                }
            }
        };

        let additionalProps = {};
        if (additionalActionCreators) {
            additionalProps = bindActionCreators(additionalActionCreators, dispatch);
        }
        
        return {
            ...additionalProps,
            ...entityProps,
        };
    };

    return { mapStateToProps, mapDispatchToProps };
};
